import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePlatformUsers } from 'src/graphql/entities/platform-user';
import {
  GetClientsQuery,
  SEARCH_CLIENTS_QUERY,
} from 'src/graphql/entities/search/search-client';
import { QueryVariables } from 'src/graphql/types';
import { ClientRaw, ConversationRaw, PlatformUserRaw } from 'src/types';

export const useRequesterGetters = (records?: ConversationRaw[]) => {
  const [isReady, setIsReady] = useState(false);
  const [clientsMap, setClientsMap] = useState<Map<string, ClientRaw>>(() => new Map());
  const [agentsMap, setAgentsMap] = useState<Map<string, PlatformUserRaw>>(() => new Map());

  const clientIdsToFetch = useMemo(() => {
    const ids = (records ?? [])
      .filter(
        (r) =>
          (r.requester?.participantEntity as ConversationParticipantEntity) ===
            ConversationParticipantEntity.Client &&
          r.requester?.participantIdentity &&
          !clientsMap.has(r.requester.participantIdentity),
      )
      .map((r) => r.requester.participantIdentity);

    return Array.from(new Set(ids)).sort();
  }, [records, clientsMap]);

  const agentIdsToFetch = useMemo(() => {
    const fromReq = (records ?? [])
      .filter(
        (r) =>
          (r.requester?.participantEntity as ConversationParticipantEntity) ===
            ConversationParticipantEntity.PlatformUser &&
          r.requester?.participantIdentity &&
          !agentsMap.has(r.requester.participantIdentity),
      )
      .map((r) => r.requester.participantIdentity);

    const fromAssignee = (records ?? [])
      .filter((r) => r.conversationAssigneeId && !agentsMap.has(r.conversationAssigneeId))
      .map((r) => r.conversationAssigneeId);

    return Array.from(new Set([...fromReq, ...fromAssignee])).sort();
  }, [records, agentsMap]);

  const { data: rawClientsData, previousData: prevClientsData } = useQuery<
    GetClientsQuery,
    QueryVariables
  >(SEARCH_CLIENTS_QUERY, {
    variables: {
      params: {
        filters: [
          {
            attribute: 'id',
            operator: 'in',
            value: clientIdsToFetch.join(','),
          },
        ],
        includeDeleted: true,
        limit: clientIdsToFetch.length,
      },
    },
    skip: clientIdsToFetch.length === 0,
    notifyOnNetworkStatusChange: true,
    context: { omitIsolationModeHeader: true },
  });

  const rawClientsMap = useMemo(() => {
    const clientRecords = (rawClientsData || prevClientsData)?.getClient?.records ?? [];
    return new Map(clientRecords.map((record) => [record.id, record]));
  }, [rawClientsData, prevClientsData]);

  const { rawPlatformUsersMap } = usePlatformUsers(
    {
      filters: [
        {
          attribute: 'id',
          operator: 'in',
          value: agentIdsToFetch.join(','),
        },
      ],
      limit: agentIdsToFetch.length,
    },
    {
      skip: agentIdsToFetch.length === 0,
      context: { omitIsolationModeHeader: true },
    },
  );

  useEffect(() => {
    // small delay to let data propagate
    setTimeout(() => setIsReady(true), 500);

    if (!rawClientsMap || rawClientsMap.size === 0) {
      return;
    }

    setClientsMap((prev) => {
      const next = new Map(prev);

      for (const [id, client] of rawClientsMap) {
        if (client) {
          next.set(id, client);
        }
      }

      return next;
    });
  }, [rawClientsMap]);

  useEffect(() => {
    // small delay to let data propagate
    setTimeout(() => setIsReady(true), 500);

    if (!rawPlatformUsersMap || rawPlatformUsersMap.size === 0) {
      return;
    }

    setAgentsMap((prev) => {
      const next = new Map(prev);

      for (const [id, user] of rawPlatformUsersMap) {
        if (user) {
          next.set(id, user);
        }
      }

      return next;
    });
  }, [rawPlatformUsersMap]);

  const getClient = useCallback((id: string) => clientsMap.get(id), [clientsMap]);

  const getAgent = useCallback((id: string) => agentsMap.get(id), [agentsMap]);

  return {
    getClient,
    getAgent,
    loading: records?.length && !isReady,
  };
};
